import type {ComponentPropsWithoutRef} from 'react';
import ReactMarkdown from 'react-markdown';
import {useMemo} from 'react';
import type {Components, ExtraProps} from 'react-markdown';
import type {PluggableList} from 'unified';
import type {Schema} from 'hast-util-sanitize';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, {defaultSchema} from 'rehype-sanitize';
import rehypeExternalLinks from 'rehype-external-links';
import remarkGfm from 'remark-gfm';
import {cn} from '../lib/utils';

export interface MarkdownRendererProps {
  content: string;
  className?: string;
  remarkPlugins?: PluggableList;
  rehypePlugins?: PluggableList;
  components?: Partial<Components>;
  sanitizeSchema?: Schema;
}

const DEFAULT_REMARK_PLUGINS = [remarkGfm];
const DEFAULT_REHYPE_PLUGINS = [rehypeRaw];

/**
 * Tables take whatever width their content needs and scroll inside their own
 * container: the surrounding page must never scroll horizontally for them.
 * The wrapper carries prose's table margins, which no longer collapse into
 * the neighbouring paragraphs once they sit inside a scroll container.
 */
export const MarkdownTable = ({
  node: _node,
  ...props
}: ComponentPropsWithoutRef<'table'> & ExtraProps) => (
  <div className="my-[2em] overflow-x-auto first:mt-0 last:mb-0 [&>table]:my-0">
    <table {...props} />
  </div>
);

export const MarkdownRenderer = ({
  content,
  className,
  remarkPlugins,
  rehypePlugins,
  components,
  sanitizeSchema,
}: MarkdownRendererProps) => {
  const resolvedRemarkPlugins = useMemo<PluggableList>(
    () => [...DEFAULT_REMARK_PLUGINS, ...(remarkPlugins ?? [])],
    [remarkPlugins],
  );
  const resolvedRehypePlugins = useMemo<PluggableList>(() => {
    const resolvedSanitizeSchema = sanitizeSchema ?? defaultSchema;
    return [
      ...DEFAULT_REHYPE_PLUGINS,
      [rehypeSanitize, resolvedSanitizeSchema],
      [
        rehypeExternalLinks,
        {rel: ['noopener', 'noreferrer'], target: '_blank'},
      ],
      ...(rehypePlugins ?? []),
    ];
  }, [rehypePlugins, sanitizeSchema]);

  return (
    <div className={cn('prose', className)}>
      <ReactMarkdown
        remarkPlugins={resolvedRemarkPlugins}
        rehypePlugins={resolvedRehypePlugins}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
